import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Building2, RefreshCcw, Shield, Monitor, CheckCircle2 } from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: Building2,
      title: 'PFI & Lifecycle Advisory',
      description: 'Specialist support for organisations managing long-term PFI contracts and complex lifecycle obligations.',
      deliverables: [
        'Lifecycle planning and capital works strategy',
        'Governance framework design',
        'Financial forecasting and risk management',
        'Stakeholder reporting structures',
        'CDM and H&S compliance'
      ],
      suited: 'NHS Trusts, FM providers, PFI SPVs'
    },
    {
      icon: RefreshCcw,
      title: 'Delivery Transformation',
      description: 'For organisations ready to professionalise their project delivery and build scalable capability.',
      deliverables: [
        'Operating model design',
        'PMO setup and capability building',
        'Process standardisation',
        'Team structure optimisation',
        'Performance measurement frameworks'
      ],
      suited: 'Growing organisations, new PMOs'
    },
    {
      icon: Shield,
      title: 'Governance & Assurance',
      description: 'Give your board and stakeholders confidence that programmes are under control.',
      deliverables: [
        'Governance structure design',
        'Stage-gate frameworks',
        'Risk and issue protocols',
        'Independent assurance reviews',
        'Compliance support'
      ],
      suited: 'Boards, executives, compliance teams'
    },
    {
      icon: Monitor,
      title: 'Videra Implementation',
      description: 'Get your portfolio onto the Videra platform with expert configuration and support.',
      deliverables: [
        'Platform setup and configuration',
        'Data migration',
        'User training',
        'Change management support',
        'Ongoing optimisation'
      ],
      suited: 'Videra customers',
      cta: { text: 'Learn more about Videra', link: '/videra' }
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-6">
              Services
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 leading-[1.1]">
              Consultancy that delivers
            </h1>
            <p className="mt-8 text-lg lg:text-xl text-slate-600 leading-relaxed">
              Practical, hands-on support for organisations managing complex 
              project portfolios. We work alongside your teams to implement 
              real change — not just write reports.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="pb-20 md:pb-32" data-testid="services-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-slate-200 p-8 md:p-12 hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300"
                data-testid={`service-${index}`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                  {/* Header */}
                  <div className="lg:col-span-5">
                    <div className="flex items-start gap-6">
                      <div className="w-14 h-14 bg-slate-900 flex items-center justify-center flex-shrink-0">
                        <service.icon className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl md:text-3xl font-medium text-slate-900 leading-tight">
                          {service.title}
                        </h2>
                        <p className="mt-3 text-slate-600">
                          {service.description}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Deliverables */}
                  <div className="lg:col-span-7">
                    <p className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold mb-4">
                      What we deliver
                    </p>
                    <ul className="space-y-3">
                      {service.deliverables.map((item, dIndex) => (
                        <li key={dIndex} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-600">{item}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-[0.1em] text-slate-400 font-semibold">
                          Best suited for
                        </p>
                        <p className="text-slate-700 mt-1">{service.suited}</p>
                      </div>
                      
                      {service.cta && (
                        <Link
                          to={service.cta.link}
                          className="inline-flex items-center text-slate-900 font-medium hover:text-blue-600 transition-colors group"
                          data-testid={`service-cta-${index}`}
                        >
                          {service.cta.text}
                          <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-20 md:py-32 bg-slate-50 border-y border-slate-200" data-testid="how-we-work">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mb-16"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">
              Our Approach
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">
              How we work
            </h2>
            <p className="mt-6 text-lg text-slate-600">
              We believe in practical delivery over lengthy analysis. Our 
              engagements are designed to create real change, fast.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { 
                step: '01', 
                title: 'Understand', 
                desc: 'We start by understanding your specific challenges, constraints, and goals. No generic solutions.' 
              },
              { 
                step: '02', 
                title: 'Design', 
                desc: 'We design practical frameworks and approaches tailored to your organisation and culture.' 
              },
              { 
                step: '03', 
                title: 'Embed', 
                desc: 'We work alongside your teams to implement change that sticks — building capability as we go.' 
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 border border-slate-200"
                data-testid={`process-step-${index}`}
              >
                <div className="text-4xl font-medium text-slate-200 mb-4">{item.step}</div>
                <h3 className="text-xl font-medium text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">
              Let's discuss your needs
            </h2>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto">
              Every organisation is different. Get in touch to explore how 
              we can help with your specific challenges.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid="services-contact-cta"
                className="inline-flex items-center justify-center bg-slate-900 text-white px-10 py-5 font-medium tracking-wide hover:bg-slate-800 transition-all group"
              >
                Get in Touch
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/videra"
                data-testid="services-videra-cta"
                className="inline-flex items-center justify-center bg-transparent border border-slate-200 text-slate-900 px-10 py-5 font-medium tracking-wide hover:border-slate-900 transition-all"
              >
                Explore Videra Platform
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
